import { useEffect, useState } from "react";
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  fetchAndActivate,
  getRemoteConfig,
  getString,
  type RemoteConfig,
} from "firebase/remote-config";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
} as const;

let firebaseApp: FirebaseApp | null = null;
let remoteConfigInstance: RemoteConfig | null = null;

const getFirebaseApp = () => {
  if (firebaseApp) {
    return firebaseApp;
  }

  const existingApps = getApps();
  if (existingApps.length > 0) {
    firebaseApp = existingApps[0]!;
  } else {
    firebaseApp = initializeApp(firebaseConfig);
  }

  return firebaseApp;
};

const getRemoteConfigClient = () => {
  if (remoteConfigInstance) {
    return remoteConfigInstance;
  }

  const app = getFirebaseApp();
  const rc = getRemoteConfig(app);

  rc.settings = {
    minimumFetchIntervalMillis: 60_000,
    fetchTimeoutMillis: 60_000,
  };

  rc.defaultConfig = {
    kyc_support: "https://bizbot360.com/ad-leads/kyc_support",
    customer_service: "https://bizbot360.com/ad-leads/nommia_inquiry",
    android_version: "7",
    ios_version: "7",
    ios_store: "https://testflight.apple.com/join/ZyuqsMkf",
    android_store: "https://play.google.com/apps/testing/io.nommia.mobile",
  };

  remoteConfigInstance = rc;
  return rc;
};

export const fetchRemoteConfig = async () => {
  try {
    const rc = getRemoteConfigClient();
    await fetchAndActivate(rc);
    return rc;
  } catch (error) {
    console.warn("[firebase] Failed to fetch remote config", error);
    return getRemoteConfigClient();
  }
};

export const getRemoteConfigString = async (
  key: string
): Promise<string | null> => {
  const rc = await fetchRemoteConfig();
  const value = getString(rc, key);
  return value || null;
};

export const useRemoteConfigString = (key: string) => {
  const [value, setValue] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const result = await getRemoteConfigString(key);
      if (!cancelled) {
        setValue(result);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return value;
};
